import React, { useState, useEffect, forwardRef } from 'react';
import {
  Checkbox,
  CustomPropertiesForm,
  FormTabs,
  Input,
  SelectOwner,
  InputStatus,
  Textarea,
  textareaInitialState,
  textareaToRaw,
} from '../components';
import { getBatchEditValue, Asset } from '../helpers';
import { useCustomProperties, useGlobalState, useMessage } from '../hooks';

const _AddSystemForm = ({ mode, data, submitForm, loading, setLoading, formErrors = {} }, ref) => {
  const initialFormState = {
    system_id: null,
    system_name: '',
    system_code: '',
    system_type: '',
    system_description: textareaInitialState({}),
    status_id: '',
    owner_principal_id: '',
  };

  const initialBatchEditItems = {
    batch_status_id: false,
    batch_owner_principal_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [displayNames] = useGlobalState('displayNames');
  const [statuses] = useGlobalState('statuses');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  const { hideMessage } = useMessage();

  const {
    customProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    setFormCustomPropertyValues,
    hasCustomProperties,
    getCustomPropertyValuesForSubmit,
    resetFormCustomPropertyValues,
  } = useCustomProperties({ objectName: Asset.System, objectIds: data.map(x => x.system_id) });

  useEffect(() => {
    // Get form data
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData({
        system_id: data[0].system_id,
        system_name: data[0].system_name || '',
        system_code: data[0].system_code || '',
        system_type: data[0].system_type || '',
        system_description: textareaInitialState({ value: data[0].system_description }),
        status_id: data[0].status_id,
        owner_principal_id: data[0].owner_principal_id,
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        status_id: getBatchEditValue(data, 'status_id'),
        owner_principal_id: getBatchEditValue(data, 'owner_principal_id'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser?.principal_id,
      });
    } else {
      resetForm();
    }
  }, [mode, data, loggedInUser]);

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          system_id: item.system_id,
          system_name: item.system_name,
          system_code: item.system_code,
          system_type: item.system_type,
          status_id: item.status_id,
          owner_principal_id: item.owner_principal_id,
          ...tmp,
          ...getCustomPropertyValuesForSubmit(item.system_id),
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        system_description: textareaToRaw({ value: formData.system_description }),
        ...getCustomPropertyValuesForSubmit(),
      };
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    resetFormCustomPropertyValues();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setFormData({
      ...initialFormState,
      status_id: statuses.default_system_status_id,
      owner_principal_id: loggedInUser ? loggedInUser.principal_id : null,
    });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="system_name"
            value={formData.system_name}
            statusValue={formData.status_id}
            assetCategory="system"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={
              formErrors.system_name ? { valid: false, message: formErrors.system_name } : undefined
            }
          />
        </div>
        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            {!batchEdit && (
              <div className="form-block vertical">
                <Input
                  type="text"
                  name="system_code"
                  value={formData.system_code}
                  onChange={handleInputChange}
                  title={displayNames?.system_code.display_name_description}
                  disabled={loading}
                  validation={
                    formErrors.system_code
                      ? { valid: false, message: formErrors.system_code }
                      : undefined
                  }
                  isOptional={true}
                />
              </div>
            )}
            {!batchEdit && (
              <div className="form-block vertical">
                <Input
                  type="text"
                  name="system_type"
                  value={formData.system_type}
                  onChange={handleInputChange}
                  title={displayNames?.system_type.display_name_description}
                  disabled={loading}
                  validation={
                    formErrors.system_type
                      ? { valid: false, message: formErrors.system_type }
                      : undefined
                  }
                  isOptional={true}
                />
              </div>
            )}
            {!batchEdit && (
              <div className="form-block vertical">
                <Textarea
                  name="system_description"
                  value={formData.system_description}
                  onChange={handleInputChange}
                  disabled={loading}
                  isOptional={true}
                />
              </div>
            )}
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_owner_principal_id}
                    name="batch_owner_principal_id"
                    label="Edit Owner"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectOwner
                name="owner_principal_id"
                value={
                  formData.owner_principal_id === 'multiple'
                    ? undefined
                    : formData.owner_principal_id
                }
                label={!batchEdit}
                placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_owner_principal_id)}
              />
            </div>
          </div>

          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddSystemForm = forwardRef(_AddSystemForm);
